import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { appsAPI } from '../api/appsAPI';
import UserProfileDropdown from '../../../components/UserProfileDropdown/UserProfileDropdown';
import { Logo_black } from '../../../components/Logo/Logo';
import { API_CONFIG } from '../../../config/api';
import './WindmillGame.css';

const getGameApiUrl = (path) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_CONFIG.baseURL}${normalizedPath}`;
};

const BASE = 'https://pathbuddy-media.lon1.cdn.digitaloceanspaces.com/Games/Sputnik%20-%20Into%20the%20Void/';
const VIDEO_MAP = {
  'WordlV1.mp4': `${BASE}WordlV1.mp4`,
  'world1.1.mp4': `${BASE}world1.1.mp4`,
  'world1.2.mp4': `${BASE}world1.2.mp4`,
  'world1.3.mp4': `${BASE}world1.3.mp4`,
  'world1.4.mp4': `${BASE}world1.4.mp4`,
  'world1.5.mp4': `${BASE}world1.5`,
};

const SCENE_QUESTION_MAP = {
  'WordlV1.mp4': 'orbit_mass_constraint',
  'world1.1.mp4': 'power_signal_physics',
  'world1.2.mp4': 'power_signal_physics',
};

const SputnikGame = ({ appId: appIdProp, onComplete, parentAppId }) => {
  const { id: idFromParams } = useParams();
  const navigate = useNavigate();
  const id = appIdProp != null ? appIdProp : idFromParams;
  const { user } = useAuth();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuestion, setShowQuestion] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [currentVideo, setCurrentVideo] = useState('WordlV1.mp4');
  const [pendingVideo, setPendingVideo] = useState(null);
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewQuestionDone, setReviewQuestionDone] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [visitedScenes, setVisitedScenes] = useState(new Set(['WordlV1.mp4']));

  const videoRef = useRef(null);
  const userName = user?.name || 'Student';
  const userEmail = user?.email || 'student@pathbuddy.com';

  useEffect(() => {
    const fetchApp = async () => {
      try {
        setLoading(true);
        const appData = await appsAPI.getAppById(id);
        setApp(appData);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchApp();
  }, [id]);

  useEffect(() => {
    setProgressPct(Math.min((visitedScenes.size / 6) * 100, 100));
  }, [visitedScenes]);

  const playVideo = (filename) => {
    setCurrentVideo(filename);
    setVisitedScenes((prev) => new Set(prev).add(filename));
    const el = videoRef.current;
    if (!el) return;
    el.src = VIDEO_MAP[filename] || `/videos/${filename}`;
    el.load();
    el.play().catch(() => {});
  };

  const showCompletion = () => {
    setShowQuestion(true);
    setCurrentQuestion({
      title: 'Sputnik Mission Complete',
      subtitle: 'Final engineering reflection',
      question: 'Was Sputnik an engineering success because it lasted, or because it was first?',
      isComplete: true,
    });
  };

  const showTraversalChoice = () => {
    setShowQuestion(true);
    setCurrentQuestion({
      title: 'QUESTION 3 - FIX VARIABLES OR TEST SCENARIO',
      subtitle: 'Mathematical validation before final decision',
      question: 'Baseline case: keep variables untouched with P = 1 W and mission duration t = 22 days. Test case: run alternate scenario at P = 2 W, so duration is approximately t = 11 days. Using the relationship Total signal proportional to P * t, which option should be selected to mathematically validate the case before final conclusion?',
      options: {
        A: { text: 'Keep variables untouched and finalize now', desc: 'Use baseline calculation only: 1 * 22 = 22' },
        B: { text: 'Run test scenario before finalizing', desc: 'Compare with test calculation: 2 * 11 = 22, then answer verification' },
      },
      isTraversal: true,
    });
  };

  const fetchQuestion = async (questionId) => {
    const response = await fetch(getGameApiUrl(`/api/sputnik-game/question/${questionId}`));
    const question = await response.json();
    setCurrentQuestion(question);
    setShowQuestion(true);
  };

  const onVideoEnded = async () => {
    if (currentVideo === 'world1.3.mp4' || currentVideo === 'world1.4.mp4') {
      if (reviewMode && !reviewQuestionDone) {
        setReviewMode(false);
        await fetchQuestion('tradeoff_verification');
        return;
      }
      showTraversalChoice();
      return;
    }
    const questionId = SCENE_QUESTION_MAP[currentVideo];
    if (!questionId) {
      showCompletion();
      return;
    }
    await fetchQuestion(questionId);
  };

  const selectOption = async (choice) => {
    if (currentQuestion?.isTraversal) {
      setShowQuestion(false);
      if (choice === 'A') {
        playVideo('world1.5.mp4');
      } else {
        const nextClip = currentVideo === 'world1.3.mp4' ? 'world1.4.mp4' : 'world1.3.mp4';
        setReviewMode(true);
        playVideo(nextClip);
      }
      return;
    }

    const questionId = currentVideo === 'world1.3.mp4' || currentVideo === 'world1.4.mp4'
      ? 'tradeoff_verification'
      : SCENE_QUESTION_MAP[currentVideo];
    if (!questionId) return;

    const response = await fetch(getGameApiUrl('/api/sputnik-game/answer'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question_id: questionId, choice, user_id: user?.id || 'default' }),
    });
    const result = await response.json();
    if (questionId === 'tradeoff_verification') setReviewQuestionDone(true);
    setFeedback(result);
    setPendingVideo(result.next_video || 'world1.5.mp4');
    setShowQuestion(false);
    setShowFeedback(true);
  };

  const continueCaseStudy = () => {
    setShowFeedback(false);
    if (!pendingVideo) {
      showCompletion();
      return;
    }
    playVideo(pendingVideo);
    setPendingVideo(null);
  };

  if (loading) {
    return <div className="windmill-game-loading"><p>Loading game...</p></div>;
  }

  return (
    <div className="windmill-game-page">
      <header className="windmill-game-header">
        <div className="windmill-game-header-content">
          <div className="windmill-game-left-section">
            <button className="back-button" onClick={() => navigate(parentAppId ? `/apps-store/${parentAppId}/experience` : `/apps-store/${id}`)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" /></svg>
            </button>
            <div className="windmill-game-logo"><Logo_black onClick={() => navigate('/apps-store')} /></div>
            <h1 className="windmill-game-title">{app?.title || 'Sputnik Game'}</h1>
          </div>
        </div>
      </header>

      <main className="windmill-game-main">
        <div className="windmill-video-container">
          <video ref={videoRef} className="windmill-main-video" autoPlay muted playsInline onEnded={onVideoEnded} onLoadStart={() => setVideoLoading(true)} onCanPlay={() => setVideoLoading(false)}>
            <source src={VIDEO_MAP['WordlV1.mp4']} type="video/mp4" />
          </video>

          {showQuestion && currentQuestion && (
            <div className="windmill-overlay active">
              <div className="windmill-question-panel">
                {currentQuestion.isComplete ? (
                  <>
                    <div className="windmill-question-title">{currentQuestion.title}</div>
                    <div className="windmill-question-subtitle">{currentQuestion.subtitle}</div>
                    <div className="windmill-question-text">{currentQuestion.question}</div>
                    <button className="windmill-continue-btn" onClick={() => (onComplete ? onComplete() : navigate(parentAppId ? `/apps-store/${parentAppId}/experience` : `/apps-store/${id}`))}>Continue</button>
                  </>
                ) : (
                  <>
                    <div className="windmill-question-title">{currentQuestion.title}</div>
                    <div className="windmill-question-subtitle">{currentQuestion.subtitle}</div>
                    <div className="windmill-question-text">{currentQuestion.question}</div>
                    <div className="windmill-options">
                      {Object.keys(currentQuestion.options || {}).map((key) => (
                        <div key={key} className="windmill-option-button" onClick={() => selectOption(key)}>
                          <span className="windmill-option-letter">{key}</span>
                          <div className="windmill-option-title">{currentQuestion.options[key].text}</div>
                          <div className="windmill-option-desc">{currentQuestion.options[key].desc}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {showFeedback && feedback && (
            <div className={`windmill-feedback ${feedback.correct ? 'correct' : 'incorrect'}`}>
              <div className="windmill-feedback-title">{feedback.correct ? 'Correct technical decision' : 'Reasonable but suboptimal'}</div>
              <div className="windmill-feedback-text">{feedback.explanation}</div>
              {feedback.calculation && <div className="windmill-feedback-text"><strong>Calculation:</strong><br />{feedback.calculation}</div>}
              <button className="windmill-continue-btn" onClick={continueCaseStudy}>Continue</button>
            </div>
          )}

          {videoLoading && <div className="windmill-loading">Loading...</div>}
          <div className="windmill-progress-bar"><div className="windmill-progress-fill" style={{ width: `${progressPct}%` }}></div></div>
        </div>
      </main>
    </div>
  );
};

export default SputnikGame;
